import { useState, useRef, useEffect } from "react";
import { Shell } from "@/components/layout/Shell";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { api } from "@shared/routes";
import { VEICULO_TIPO_LABELS, VEICULO_TIPO_COLORS } from "@/lib/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Layers, Pencil, Check, X, BookOpen, Loader2, Plus } from "lucide-react";

// Inline hooks — will be replaced by useRemontaCategorias/useRemontaElementos from
// use-data.ts once plan 03-03 merges. Pattern matches use-data.ts authFetch pattern.
function useAuthFetchLocal() {
  const { session } = useAuth();
  return async (url: string) => {
    const headers: Record<string, string> = {};
    if (session?.access_token) {
      headers.Authorization = `Bearer ${session.access_token}`;
    }
    const res = await fetch(url, { headers });
    if (!res.ok) throw new Error(`Erro: ${res.statusText}`);
    return res.json();
  };
}

function useRemontaCategoriasLocal(tipoVeiculo?: string, uf?: string) {
  const authFetch = useAuthFetchLocal();
  return useQuery<any[]>({
    queryKey: [api.remontaCategorias.list.path, tipoVeiculo, uf],
    queryFn: () => {
      const params = new URLSearchParams();
      if (tipoVeiculo) params.set("tipoVeiculo", tipoVeiculo);
      if (uf) params.set("uf", uf);
      const qs = params.toString();
      return authFetch(`${api.remontaCategorias.list.path}${qs ? `?${qs}` : ""}`);
    },
    enabled: !!tipoVeiculo,
  });
}

function useRemontaElementosLocal(categoriaId?: string) {
  const authFetch = useAuthFetchLocal();
  return useQuery<any[]>({
    queryKey: [api.remontaElementos.list.path, categoriaId],
    queryFn: () => {
      const params = categoriaId ? `?categoriaId=${categoriaId}` : "";
      return authFetch(`${api.remontaElementos.list.path}${params}`);
    },
    enabled: !!categoriaId,
  });
}

// Sub-component props
interface CategoriaAccordionItemProps {
  categoria: any;
  canEdit: boolean;
  editingElementId: string | null;
  editNome: string;
  editDescricao: string;
  nomeInputRef: React.RefObject<HTMLInputElement>;
  isSaving: boolean;
  onStartEdit: (elemento: any) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onEditNomeChange: (v: string) => void;
  onEditDescricaoChange: (v: string) => void;
  // Add element
  selectedUf: string;
  addingElementCategoriaId: string | null;
  newNome: string;
  newDescricao: string;
  newUf: string;
  isCreating: boolean;
  onStartAdd: (categoriaId: string) => void;
  onCancelAdd: () => void;
  onSaveAdd: () => void;
  onNewNomeChange: (v: string) => void;
  onNewDescricaoChange: (v: string) => void;
  onNewUfChange: (v: string) => void;
  // Edit UF
  editUf: string;
  onEditUfChange: (v: string) => void;
}

function CategoriaAccordionItem({
  categoria,
  canEdit,
  editingElementId,
  editNome,
  editDescricao,
  nomeInputRef,
  isSaving,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onEditNomeChange,
  onEditDescricaoChange,
  selectedUf,
  addingElementCategoriaId,
  newNome,
  newDescricao,
  newUf,
  isCreating,
  onStartAdd,
  onCancelAdd,
  onSaveAdd,
  onNewNomeChange,
  onNewDescricaoChange,
  onNewUfChange,
  editUf,
  onEditUfChange,
}: CategoriaAccordionItemProps) {
  const { data: elementos, isLoading } = useRemontaElementosLocal(categoria.id);

  return (
    <AccordionItem value={categoria.id}>
      <div className="flex items-center gap-2">
        <AccordionTrigger className="min-w-0 flex-1 justify-start gap-2">
          <span className="font-semibold text-slate-800">{categoria.nome}</span>
          <span className="text-sm text-slate-500">({elementos?.length ?? "..."} elementos)</span>
        </AccordionTrigger>
        {canEdit && (
          <Button
            size="icon"
            variant="ghost"
            className="h-9 w-9 shrink-0"
            title="Adicionar elemento"
            aria-label={`Adicionar elemento à categoria ${categoria.nome}`}
            onClick={() => onStartAdd(categoria.id)}
          >
            <Plus className="w-3 h-3" />
          </Button>
        )}
      </div>
      <AccordionContent>
        {isLoading ? (
          <div className="space-y-2 pt-2">
            {Array(4)
              .fill(null)
              .map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
          </div>
        ) : (
          <ul className="space-y-2 pt-2">
            {(elementos ?? []).map((elemento: any) =>
              editingElementId === elemento.id ? (

                // Edit mode
                <li key={elemento.id} className="flex gap-2 items-start py-2">
                  <div className="flex-1 space-y-1">
                    <Input
                      ref={nomeInputRef}
                      value={editNome}
                      onChange={(e) => onEditNomeChange(e.target.value)}
                      className="h-8"
                      maxLength={100}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") onSaveEdit();
                        if (e.key === "Escape") onCancelEdit();
                      }}
                      aria-label="Nome do elemento"
                    />
                    <Input
                      value={editDescricao}
                      onChange={(e) => onEditDescricaoChange(e.target.value)}
                      className="h-8"
                      maxLength={300}
                      placeholder="Descrição (opcional)"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") onSaveEdit();
                        if (e.key === "Escape") onCancelEdit();
                      }}
                      aria-label="Descrição do elemento"
                    />
                    <Input
                      value={editUf}
                      onChange={(e) => onEditUfChange(e.target.value.toUpperCase().slice(0, 2))}
                      className="h-8 w-20"
                      maxLength={2}
                      placeholder="UF"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") onSaveEdit();
                        if (e.key === "Escape") onCancelEdit();
                      }}
                      aria-label="UF do elemento"
                    />
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={onSaveEdit}
                      disabled={isSaving}
                      aria-label="Salvar"
                    >
                      {isSaving ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Check className="w-3.5 h-3.5" />
                      )}
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={onCancelEdit}
                      disabled={isSaving}
                      aria-label="Cancelar"
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </li>
              ) : (
                // Display mode
                <li
                  key={elemento.id}
                  className="flex items-start justify-between gap-4 py-2"
                >
                  <div className="flex-1">
                    <p className="font-medium text-sm text-slate-800">
                      {elemento.numeroItem && <span className="mr-2 font-mono text-xs text-slate-500">{elemento.numeroItem}.</span>}
                      {elemento.nome}
                      {elemento.gravidade && <Badge variant="outline" className="ml-2">{elemento.gravidade}</Badge>}
                    </p>
                    {elemento.descricao && (
                      <p className="text-sm text-slate-500 mt-0.5">
                        {elemento.descricao}
                      </p>
                    )}
                  </div>
                  {canEdit && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => onStartEdit(elemento)}
                      aria-label={`Editar ${elemento.nome}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </li>
              )
            )}
            {addingElementCategoriaId === categoria.id && (
              <li className="flex gap-2 items-start py-2">
                <div className="flex-1 space-y-1">
                  <Input
                    autoFocus
                    placeholder="Nome do elemento *"
                    className="h-8"
                    maxLength={100}
                    value={newNome}
                    onChange={(e) => onNewNomeChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") onSaveAdd();
                      if (e.key === "Escape") onCancelAdd();
                    }}
                    aria-label="Nome do elemento"
                  />
                  <Input
                    placeholder="Descrição (opcional)"
                    className="h-8"
                    maxLength={300}
                    value={newDescricao}
                    onChange={(e) => onNewDescricaoChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") onSaveAdd();
                      if (e.key === "Escape") onCancelAdd();
                    }}
                    aria-label="Descrição"
                  />
                  <Input
                    placeholder="UF"
                    className="h-8 w-20"
                    maxLength={2}
                    value={newUf}
                    onChange={(e) => onNewUfChange(e.target.value.toUpperCase().slice(0, 2))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") onSaveAdd();
                      if (e.key === "Escape") onCancelAdd();
                    }}
                    aria-label="UF"
                  />
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={onSaveAdd}
                    disabled={isCreating || !newNome.trim()}
                    aria-label="Salvar elemento"
                  >
                    {isCreating ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Check className="w-3.5 h-3.5" />
                    )}
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={onCancelAdd}
                    disabled={isCreating}
                    aria-label="Cancelar adição"
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </li>
            )}
          </ul>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

const TIPOS = ["moto", "auto_leve", "caminhao", "onibus"] as const;
type TipoVeiculo = (typeof TIPOS)[number];

export default function Catalogo() {
  const { profile, session } = useAuth();
  const { toast } = useToast();

  const roles = profile?.roles || [];
  const isAdmin = roles.includes("admin");
  const isOwner = roles.includes("owner");

  // owner: full edit + any UF; admin: edit locked to vistoriadora UF; others: read-only
  const canEdit = isOwner || isAdmin;
  const vistoriadoraUf: string = (profile?.empresa as any)?.uf ?? "MG";
  const lockedUf = !isOwner ? vistoriadoraUf : null;

  const [selectedTipo, setSelectedTipo] = useState<TipoVeiculo>("moto");
  const [selectedUf, setSelectedUf] = useState<string>(lockedUf ?? "MG");
  const [catalogScope, setCatalogScope] = useState<"federal" | "complementar">("federal");
  const [editingElementId, setEditingElementId] = useState<string | null>(null);
  const [editNome, setEditNome] = useState("");
  const [editDescricao, setEditDescricao] = useState("");
  const [editUf, setEditUf] = useState("");
  const nomeInputRef = useRef<HTMLInputElement>(null);
  // Add element state
  const [addingElementCategoriaId, setAddingElementCategoriaId] = useState<string | null>(null);
  const [newNome, setNewNome] = useState("");
  const [newDescricao, setNewDescricao] = useState("");
  const [newUf, setNewUf] = useState(selectedUf);
  const [openCategories, setOpenCategories] = useState<string[]>([]);

  // Focus nome input when entering edit mode
  useEffect(() => {
    if (editingElementId && nomeInputRef.current) {
      nomeInputRef.current.focus();
    }
  }, [editingElementId]);

  // Sync newUf when selectedUf changes
  useEffect(() => {
    setNewUf(selectedUf);
  }, [selectedUf]);

  const { data: categorias, isLoading: categoriasLoading } =
    useRemontaCategoriasLocal(selectedTipo, catalogScope === "federal" ? "BR" : selectedUf);

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      nome,
      descricao,
      uf,
    }: {
      id: string;
      nome: string;
      descricao: string;
      uf: string;
    }) => {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (session?.access_token)
        headers.Authorization = `Bearer ${session.access_token}`;
      // Build path manually: replace :id in the path template
      const path = api.remontaElementos.update.path.replace(":id", id);
      const res = await fetch(path, {
        method: "PUT",
        headers,
        body: JSON.stringify({ nome, descricao, uf }),
      });
      if (!res.ok) {
        const err = await res
          .json()
          .catch(() => ({ message: "Erro ao salvar. Tente novamente." }));
        throw new Error(err.message);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [api.remontaElementos.list.path],
      });
      toast({ title: "Elemento atualizado." });
      setEditingElementId(null);
    },
    onError: (err: Error) => {
      toast({ title: err.message, variant: "destructive" });
    },
  });

  const createElementoMutation = useMutation({
    mutationFn: async ({
      categoriaId,
      tipoVeiculo,
      nome,
      descricao,
      uf,
    }: {
      categoriaId: string;
      tipoVeiculo: string;
      nome: string;
      descricao: string;
      uf: string;
    }) => {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (session?.access_token)
        headers.Authorization = `Bearer ${session.access_token}`;
      const res = await fetch(api.remontaElementos.create.path, {
        method: "POST",
        headers,
        body: JSON.stringify({ categoriaId, tipoVeiculo, nome, descricao, uf, sequencia: 0 }),
      });
      if (!res.ok) {
        const err = await res
          .json()
          .catch(() => ({ message: "Erro ao adicionar elemento." }));
        throw new Error(err.message);
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [api.remontaElementos.list.path],
      });
      toast({ title: "Elemento adicionado." });
      setAddingElementCategoriaId(null);
      setNewNome("");
      setNewDescricao("");
      setNewUf(selectedUf);
    },
    onError: (err: Error) => {
      toast({ title: err.message, variant: "destructive" });
    },
  });

  const startEdit = (elemento: any) => {
    setEditingElementId(elemento.id);
    setEditNome(elemento.nome);
    setEditDescricao(elemento.descricao ?? "");
    setEditUf(elemento.uf ?? "MG");
    setAddingElementCategoriaId(null);
  };

  const cancelEdit = () => {
    setEditingElementId(null);
    setEditNome("");
    setEditDescricao("");
    setEditUf("");
  };

  const saveEdit = () => {
    if (!editingElementId || !editNome.trim()) return;
    updateMutation.mutate({
      id: editingElementId,
      nome: editNome.trim(),
      descricao: editDescricao.trim(),
      uf: editUf.trim() || "MG",
    });
  };

  const startAdd = (categoriaId: string) => {
    setAddingElementCategoriaId(categoriaId);
    setEditingElementId(null);
    setNewNome("");
    setNewDescricao("");
    setNewUf(selectedUf);
    setOpenCategories((prev) =>
      prev.includes(categoriaId) ? prev : [...prev, categoriaId]
    );
  };

  const cancelAdd = () => {
    setAddingElementCategoriaId(null);
    setNewNome("");
    setNewDescricao("");
    setNewUf(selectedUf);
  };

  const saveAdd = () => {
    if (!addingElementCategoriaId || !newNome.trim()) return;
    const categoria = categorias?.find((c: any) => c.id === addingElementCategoriaId);
    if (!categoria) return;
    createElementoMutation.mutate({
      categoriaId: addingElementCategoriaId,
      tipoVeiculo: categoria.tipoVeiculo,
      nome: newNome.trim(),
      descricao: newDescricao.trim(),
      uf: newUf || selectedUf,
    });
  };


  return (
    <Shell>
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Catálogo Estrutural</h1>
      </div>

      <Tabs value={catalogScope} onValueChange={(value) => { setCatalogScope(value as "federal" | "complementar"); setEditingElementId(null); setAddingElementCategoriaId(null); setOpenCategories([]); }} className="mb-5">
        <TabsList className="grid w-full grid-cols-2 sm:w-[420px]">
          <TabsTrigger value="federal">Matriz CONTRAN</TabsTrigger>
          <TabsTrigger value="complementar">Complementar por UF</TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Vehicle type selector */}
      <div className="flex gap-2 mb-6 flex-wrap items-center">
        {TIPOS.map((tipo) => (
          <Button
            key={tipo}
            variant={selectedTipo === tipo ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setSelectedTipo(tipo);
              setEditingElementId(null);
            }}
            className="gap-2"
          >
            <span
              className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${VEICULO_TIPO_COLORS[tipo]}`}
            >
              {VEICULO_TIPO_LABELS[tipo]}
            </span>
          </Button>
        ))}
        {catalogScope === "federal" ? (
          <span className="ml-auto rounded border bg-slate-50 px-3 py-1.5 text-sm font-medium text-slate-600">BR</span>
        ) : isOwner ? (
          <Select
            value={selectedUf}
            onValueChange={(val) => {
              setSelectedUf(val);
              setEditingElementId(null);
              setOpenCategories([]);
            }}
          >
            <SelectTrigger className="w-24 ml-auto" aria-label="Filtrar por estado (UF)">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="MG">MG</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <span className="ml-auto text-sm font-medium text-slate-600 border rounded px-3 py-1.5 bg-slate-50">
            {selectedUf}
          </span>
        )}
      </div>

      {/* Catalog Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Badge className={VEICULO_TIPO_COLORS[selectedTipo]}>
              {VEICULO_TIPO_LABELS[selectedTipo]}
            </Badge>
            {catalogScope === "federal" ? "Matriz CONTRAN" : "Catálogo complementar"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {categoriasLoading ? (
            <div className="space-y-2">
              {Array(3)
                .fill(null)
                .map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
            </div>
          ) : !categorias || categorias.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <p className="text-sm text-slate-500">
                Nenhuma categoria encontrada para este tipo de veículo.
              </p>
            </div>
          ) : (
            <Accordion type="multiple" value={openCategories} onValueChange={setOpenCategories} className="w-full">
              {categorias.map((categoria) => (
                <CategoriaAccordionItem
                  key={categoria.id}
                  categoria={categoria}
                  canEdit={canEdit && catalogScope === "complementar"}
                  editingElementId={editingElementId}
                  editNome={editNome}
                  editDescricao={editDescricao}
                  editUf={editUf}
                  nomeInputRef={nomeInputRef}
                  isSaving={updateMutation.isPending}
                  onStartEdit={startEdit}
                  onCancelEdit={cancelEdit}
                  onSaveEdit={saveEdit}
                  onEditNomeChange={setEditNome}
                  onEditDescricaoChange={setEditDescricao}
                  onEditUfChange={setEditUf}
                  selectedUf={selectedUf}
                  addingElementCategoriaId={addingElementCategoriaId}
                  newNome={newNome}
                  newDescricao={newDescricao}
                  newUf={newUf}
                  isCreating={createElementoMutation.isPending}
                  onStartAdd={startAdd}
                  onCancelAdd={cancelAdd}
                  onSaveAdd={saveAdd}
                  onNewNomeChange={setNewNome}
                  onNewDescricaoChange={setNewDescricao}
                  onNewUfChange={setNewUf}
                />
              ))}
            </Accordion>
          )}
        </CardContent>
      </Card>
    </Shell>
  );
}
